import {
  Button,
  CircularProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { SyncIcon } from "components/icons";
import { memo, useMemo } from "react";
import type { AskHelenaMessage } from "shared";
import { SafeHtmlRenderer } from "shared/features/chat/SafeHtmlRenderer";
import { answerLinkSx } from "../utils/answerLinkStyles";
import type { MarkdownTableSegment } from "../utils/markdownSegments";
import { parseMarkdownSegments } from "../utils/markdownSegments";
import { markdownTableSx } from "../utils/markdownTableStyles";
import { markdownToHtml } from "../utils/markdownToHtml";
import { AskHelenaAnswerTable } from "./AskHelenaAnswerTable";

type RenderSegment =
  | { type: "html"; id: string; html: string }
  | { type: "table"; id: string; table: MarkdownTableSegment };

interface InterruptedNoticeProps {
  hasContent: boolean;
  onResume?: (() => void) | null;
  isResuming: boolean;
}

const InterruptedNotice = ({
  hasContent,
  onResume,
  isResuming,
}: InterruptedNoticeProps) => {
  const theme = useTheme();

  const label = isResuming
    ? "Catching up on what Helena finished…"
    : hasContent
      ? "Helena kept working after you left this chat."
      : "This answer was interrupted when you left the chat.";

  return (
    <Stack
      direction="row"
      sx={{ alignItems: "center", gap: "8px", flexWrap: "wrap" }}
    >
      {isResuming ? (
        <CircularProgress
          size={12}
          aria-label="Catching up on this answer"
          sx={{ color: theme.palette.lightBg.low }}
        />
      ) : null}
      <Typography
        variant="body-small"
        sx={{ color: theme.palette.lightBg.low }}
      >
        {label}
      </Typography>
      {onResume && !isResuming ? (
        <Button
          variant="text"
          onClick={onResume}
          startIcon={<SyncIcon sx={{ fontSize: "14px" }} />}
          sx={{
            ...theme.typography["body-small"],
            p: "0px",
            minWidth: "auto",
            textTransform: "none",
            color: theme.palette.primary.tint,
            ":hover": { backgroundColor: "transparent" },
          }}
        >
          Refresh
        </Button>
      ) : null}
    </Stack>
  );
};

interface AskHelenaMessageBubbleProps {
  message: AskHelenaMessage;
  onResume?: (() => void) | null;
  isResuming?: boolean;
}

/** memo() because: rendered in a list that re-renders on every appended token. */
export const AskHelenaMessageBubble = memo(
  ({ message, onResume, isResuming = false }: AskHelenaMessageBubbleProps) => {
    const theme = useTheme();
    const isUser = message.role === "user";

    // Only assistant answers are Markdown. Keeping the user's own text off the
    // HTML path avoids rendering whatever they happened to type as markup.
    //
    // GFM tables are split out of the prose so they can render as real React
    // rows the user can sort; everything between them stays on the HTML path.
    const segments: RenderSegment[] = useMemo(() => {
      if (isUser) return [];

      return parseMarkdownSegments(message.content).map((segment) =>
        segment.type === "table"
          ? { type: "table", id: segment.id, table: segment }
          : {
              type: "html",
              id: segment.id,
              html: markdownToHtml(segment.markdown, message.ticketRefs),
            },
      );
    }, [isUser, message.content, message.ticketRefs]);

    // Any table still on the HTML path — one written as raw `<table>` markup —
    // keeps the card treatment the sortable renderer applies to itself.
    const tableSx = useMemo(() => markdownTableSx(theme), [theme]);
    const linkSx = useMemo(() => answerLinkSx(theme), [theme]);

    if (
      !(isUser || message.content || message.isStopped || message.isInterrupted)
    ) {
      return null;
    }

    return (
      <Stack
        sx={{
          gap: "12px",
          maxWidth: isUser ? "70%" : "100%",
          alignSelf: isUser ? "flex-end" : "flex-start",
          ...(isUser && {
            p: "12px",
            borderRadius: "8px",
            backgroundColor: theme.palette.primitive.system.violet[50],
          }),
        }}
      >
        {isUser ? (
          <Typography
            variant="body-medium"
            sx={{
              color: theme.palette.lightBg.medium,
              whiteSpace: "pre-wrap",
            }}
          >
            {message.content}
          </Typography>
        ) : (
          <>
            {segments.map((segment) =>
              segment.type === "table" ? (
                <AskHelenaAnswerTable
                  key={segment.id}
                  table={segment.table}
                  ticketRefs={message.ticketRefs}
                />
              ) : (
                <SafeHtmlRenderer
                  key={segment.id}
                  html={segment.html}
                  showMore={false}
                  variant="body-medium"
                  sx={[{ color: theme.palette.lightBg.high }, tableSx, linkSx]}
                />
              ),
            )}
            {message.isStopped ? (
              <Typography
                variant="body-small"
                sx={{ color: theme.palette.lightBg.low }}
              >
                Response stopped
              </Typography>
            ) : null}

            {message.isInterrupted ? (
              <InterruptedNotice
                hasContent={Boolean(message.content)}
                onResume={onResume}
                isResuming={isResuming}
              />
            ) : null}
          </>
        )}
      </Stack>
    );
  },
);

AskHelenaMessageBubble.displayName = "AskHelenaMessageBubble";
